//from landlord
import React, {useState, useEffect} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    CircularProgress,
    Chip,
    Alert
} from '@mui/material';
import {Edit} from 'react-feather';
import TicketHeader from './TicketHeader';
import TicketDetails from './TicketDetails';
import TicketEditForm from './TicketEditForm';
import CommentsSection from './CommentsSection';
import AttachmentsSection from './AttachmentSection';
import {statusColors, defaultStatus} from './mockData';
import {ApiDetails, KEY} from "../../Constants";
import CryptoJS from "crypto-js";
import { handle401Unauthorized, handle403Forbidden } from '../../utils/authErrorHandler';


const TicketDetailDialog = ({
                                open,
                                ticket,
                                onClose,
                                onUpdate,
                                onAddComment,
                                staff = [],
                                showAlert
                            }) => {
    const [editMode, setEditMode] = useState(false);
    const [editedTicket, setEditedTicket] = useState(null);
    const [currentTicket, setCurrentTicket] = useState(null); // New state to hold the current ticket data
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
const [originalText, setOriginalText] = useState(() => {
    const encryptedData = localStorage.getItem("myMap");
    return encryptedData 
        ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
        : "";
});;

    // Get landlord ID
    const getLandlordId = () => {
        try {
            let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("myMap"),
                KEY
            );
            let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText)[0][1];
        } catch (error) {
            console.error("Error getting landlord ID:", error);
            setError("Unable to get landlord ID. Please try again.");
            return null;
        }
    };

    // Initialize edited ticket when the dialog opens
    useEffect(() => {
        if (open && ticket) {
            setEditedTicket({...ticket});
            setCurrentTicket({...ticket}); // Initialize current ticket

            // If ticket is first opened, fetch latest data
            if (ticket && ticket.id) {
                fetchTicketDetails(ticket.id);
            }
        }
    }, [open, ticket]);

    // Fetch ticket details
    const fetchTicketDetails = async (ticketId) => {
        try {
            setLoading(true);
            const landlordId = getLandlordId();
            if (!landlordId) return;

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/get", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: JSON.stringify({
                    ticket_id: ticketId,
                    landlord_id: landlordId
                })
            });

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                const ticketData = await response.json();

                // Use onUpdate to update parent component's state
                onUpdate?.(ticketData);

                // Update local state - both currentTicket and editedTicket
                setCurrentTicket(ticketData);
                setEditedTicket(editMode ? editedTicket : {...ticketData}); // Only update edited ticket if not in edit mode
                setError(null);

                console.log("Ticket data updated:", ticketData);
            } else if (response.status === 404) {
                showAlert?.("Ticket not found", "error");
                onClose();
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error fetching ticket details:", error);
            showAlert?.("Failed to load ticket details", "error");
            setError("Failed to load ticket details");
        } finally {
            setLoading(false);
        }
    };

    // Save edited ticket
    const saveTicketChanges = async () => {
        try {
            setLoading(true);
            const landlordId = getLandlordId();
            if (!landlordId) return;

            const updatedTicket = {
                ...editedTicket,
                landlord_id: landlordId
            };

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/update", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: JSON.stringify(updatedTicket)
            });

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                // Update parent component's state
                onUpdate?.(editedTicket);
                setEditMode(false);
                showAlert?.("Ticket updated successfully", "success");
                // Refresh ticket details
                fetchTicketDetails(editedTicket.id);
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error updating ticket:", error);
            showAlert?.("Failed to update ticket", "error");
            setError("Failed to update ticket");
        } finally {
            setLoading(false);
        }
    };

    // Add comment handler (will be passed down to CommentsSection)
    const handleAddComment = async (commentText, isInternal) => {
        try {
            const landlordId = getLandlordId();
            if (!landlordId) return false;

            const commentData = {
                issue_id: currentTicket.id,
                comment: commentText,
                author_id: landlordId,
                author_type: "Landlord",
                is_internal: isInternal
            };

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/comment/add", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: JSON.stringify(commentData)
            });

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                // Refresh the entire ticket data to get updated comments
                fetchTicketDetails(currentTicket.id);

                // Call callback if provided
                onAddComment?.(currentTicket.id, commentText, isInternal);
                showAlert?.(`Comment added successfully${isInternal ? ' (Internal)' : ''}`, "success");
                return true;
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error adding comment:", error);
            showAlert?.("Failed to add comment", "error");
            return false;
        }
    };

    // File upload handler (will be passed down to AttachmentsSection)
    const handleFileUpload = async (file) => {
        try {
            const landlordId = getLandlordId();
            if (!landlordId) return false;

            // Create FormData
            const formData = new FormData();
            formData.append('file', file);
            formData.append('issue_id', currentTicket.id);
            formData.append('uploaded_by', landlordId);
            formData.append('uploaded_by_type', 'Landlord');

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/attachment/upload", {
                method: "POST",
                headers: {
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: formData
            });

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                showAlert?.("File uploaded successfully", "success");
                // Refresh ticket details to get updated attachments
                fetchTicketDetails(currentTicket.id);
                return true;
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            showAlert?.("Failed to upload file", "error");
            return false;
        }
    };

    // Toggle edit mode
    const toggleEditMode = () => {
        if (editMode) {
            // Cancel editing, reset to original ticket
            setEditedTicket({...currentTicket});
        }
        setEditMode(!editMode);
    };

    // Get status icon component
    const getStatusIcon = (status) => {
        if (!status) return null;
        const statusInfo = statusColors[status] || defaultStatus;
        return statusInfo.icon;
    };

    if (!currentTicket || !editedTicket) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{py: 2, px: 3, backgroundColor: '#f8fafc'}}>
                <TicketHeader
                    ticket={currentTicket}
                    editMode={editMode}
                    toggleEditMode={toggleEditMode}
                    loading={loading}
                    saveChanges={saveTicketChanges}
                />
            </DialogTitle>

            <DialogContent sx={{p: 0}}>
                {error && <Alert severity="error" sx={{m: 2}}>{error}</Alert>}

                {loading && !editMode ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                        <CircularProgress/>
                    </Box>
                ) : (
                    <Box sx={{p: 3}}>
                        {editMode ? (
                            <TicketEditForm
                                ticket={editedTicket}
                                setTicket={setEditedTicket}
                                staff={staff}
                            />
                        ) : (
                            <TicketDetails ticket={currentTicket}/>
                        )}

                        <AttachmentsSection
                            ticket={currentTicket}
                            onUpload={handleFileUpload}
                        />

                        <CommentsSection
                            ticket={currentTicket}
                            onAddComment={handleAddComment}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{py: 2, px: 3, borderTop: '1px solid #e2e8f0'}}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none'
                    }}
                    disabled={loading}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TicketDetailDialog;